import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";

const PrivacyPage = () => {
  return (
    <div className="pt-32" data-testid="privacy-page">
      <SEO 
        title="Privacy Policy"
        description="Learn how OptimaGrowth collects, uses, and protects your personal information. Our privacy policy outlines your rights and our data practices."
        keywords="privacy policy, data protection, personal information, OptimaGrowth privacy"
      />
      <section className="section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl font-bold text-optima-dark mb-6">
                Privacy Policy
              </h1>
              <p className="text-slate-600 mb-8">
                Last updated: January 2026
              </p>

              <div className="prose max-w-none">
                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  1. Introduction
                </h2>
                <p className="text-slate-700 mb-4">
                  OptimaGrowth ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website optimagrowth.agency and use our services.
                </p>
                <p className="text-slate-700 mb-4">
                  Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  2. Information We Collect
                </h2>
                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  2.1 Personal Information
                </h3>
                <p className="text-slate-700 mb-4">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Fill out a contact form</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Request a quote or consultation</li>
                  <li>Download our resources</li>
                  <li>Communicate with us via email or other channels</li>
                </ul>
                <p className="text-slate-700 mb-4">
                  This information may include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Company name</li>
                  <li>Job title</li>
                  <li>Business information</li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  2.2 Automatically Collected Information
                </h3>
                <p className="text-slate-700 mb-4">
                  When you visit our website, we may automatically collect certain information, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website</li>
                  <li>Device information</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  3. How We Use Your Information
                </h2>
                <p className="text-slate-700 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Respond to your inquiries and provide customer service</li>
                  <li>Send you marketing communications (with your consent)</li>
                  <li>Deliver requested resources and materials</li>
                  <li>Improve our website and services</li>
                  <li>Analyze usage patterns and trends</li>
                  <li>Comply with legal obligations</li>
                  <li>Protect against fraudulent or illegal activity</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  4. Information Sharing
                </h2>
                <p className="text-slate-700 mb-4">
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li><strong>Service Providers:</strong> We may share information with third-party service providers who perform services on our behalf (e.g., email marketing, analytics, hosting).</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights.</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred.</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  5. Data Security
                </h2>
                <p className="text-slate-700 mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  6. Your Rights
                </h2>
                <p className="text-slate-700 mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Delete your personal information</li>
                  <li>Object to or restrict processing</li>
                  <li>Data portability</li>
                  <li>Withdraw consent</li>
                </ul>
                <p className="text-slate-700 mb-4">
                  To exercise these rights, please contact us at info@optimagrowth.agency.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  7. Cookies
                </h2>
                <p className="text-slate-700 mb-4">
                  We use cookies and similar tracking technologies. For more information, please see our <Link to="/cookies" className="text-optima-blue hover:underline">Cookie Policy</Link>.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  8. Third-Party Links
                </h2>
                <p className="text-slate-700 mb-4">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  9. Children's Privacy
                </h2>
                <p className="text-slate-700 mb-4">
                  Our services are not directed to individuals under 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  10. International Data Transfers
                </h2>
                <p className="text-slate-700 mb-4">
                  If you are accessing our services from outside South Africa, please be aware that your information may be transferred to, stored, and processed in South Africa or other countries where our service providers operate.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  11. Changes to This Policy
                </h2>
                <p className="text-slate-700 mb-4">
                  We may update this privacy policy from time to time. The updated version will be indicated by an updated "Last updated" date. We encourage you to review this policy periodically.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  12. Contact Us
                </h2>
                <p className="text-slate-700 mb-4">
                  If you have questions about this Privacy Policy or our privacy practices, please contact us:
                </p>
                <p className="text-slate-700 mb-4">
                  <strong>OptimaGrowth</strong><br />
                  Email: info@optimagrowth.agency<br />
                  Phone: +27 66 122 9790<br />
                  Location: Cape Town, South Africa
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
